import { not } from "@components/smart/FieldInfo";
import { AttachmentsDef, withDisplayName } from "@components/smart/GeneralFieldDefinition";
import { FilterBarGroup, getDefaultFilterGroupDef } from "@components/smart/smartFilterBar/SmartFilterBar.types";
import { TSmartODataTableStorage } from "@components/smart/smartTable/SmartODataTableBase";
import { IToolbarItem } from "@components/toolbar/Toolbar.types";
import { createPath } from "@odata/BindingContext";
import { UserPermissionCodes } from "@odata/CompanyPermissionEnums";
import {
    EntitySetName,
    EntityTypeName,
    IPrSubmissionEntity,
    PrEmployeeEntity,
    PrHealthInsuranceCompanyEntity,
    PrSubmissionEntity
} from "@odata/GeneratedEntityTypes";
import { PrSubmissionStatusCode, PrSubmissionSubtypeCode, PrSubmissionTypeCode } from "@odata/GeneratedEnums";
import { getEnumDisplayValue } from "@odata/GeneratedEnums.utils";
import { IFormatOptions } from "@odata/OData.utils";
import { getNumberOursSummaryDef, getNumberRangeFieldDefs } from "@pages/numberRange/NumberRange.utils";
import { getEmployeeFilterDef, getEmployeeTableColumnDefs } from "@pages/payroll/employee/Employee.utils";
import {
    getColorFromStatus,
    getEmployeeSubmissionSummaryItemDef,
    getFilterForTabId,
    getMainTypeLabel,
    getSubmissionEntityType,
    getSubTypeLabel,
    PrSubmissionEnumTranslationNamespaces,
    PrSubmissionTabs
} from "@pages/payroll/submission/Submission.utils";
import { isNewEntity } from "@utils/definition.utils";
import { isDefined } from "@utils/general";
import i18next from "i18next";
import React, { ComponentType } from "react";

import { EMPTY_DASH } from "../../../constants";
import { IAppContext } from "../../../contexts/appContext/AppContext.types";
import { Sort, ToolbarItemType } from "../../../enums";
import { ColoredText } from "../../../global.style";
import { TValue } from "../../../global.types";
import { Model } from "../../../model/Model";
import { IFormDef } from "../../../views/formView/Form";
import { ISplitPageTableDef, SECONDARY_FILTERS_ALL } from "../../../views/table/TableView.utils";
import { setDefByEntityType } from "../../getDefByEntityType";
import { getItemBreadCrumbsText, IDefinition, IGetDefinition, TFieldDefinition } from "../../PageUtils";

export enum PrSubmissionGroup {
    Main = "Main",
    Identifier = "Identifier",
    Employee = "Employee",
    Employer = "Employer",
    IncomeInPeriod = "IncomeInPeriod",
    PaymentMethod = "PaymentMethod",
    BenefitRequest = "BenefitRequest",
    BenefitRequestFooter = "BenefitRequestFooter",
    CareReceiver = "CareReceiver",
    CaregivingIntervals = "CaregivingIntervals",
    BenefitPaymentInputs = "BenefitPaymentInputs",
    BenefitPaymentInputsWorkedInDays = "BenefitPaymentInputsWorkedInDays",
    BenefitPaymentInputsScheduledDays = "BenefitPaymentInputsScheduledDays",
    BenefitPaymentInputsReturnToWork = "BenefitPaymentInputsReturnToWork",
    CaregivingEvents = "CaregivingEvents",
    AdditionalInfo = "AdditionalInfo",
    Attachments = "Attachments",
    OZUSPOJDetail = "OZUSPOJDetail",
    ContactWorker = "ContactWorker",
    Foreigner = "Foreigner",
    PermanentAddress = "PermanentAddress",
    CzechAddress = "CzechAddress",
    CountryOfResidency = "CountryOfResidency",
    TaxResidenceAddress = "TaxResidenceAddress",
    Disability = "Disability",
    HealthInsurance = "HealthInsurance",
    Employment = "Employment",
    ForeignCarrier = "ForeignCarrier",
    Pension = "Pension",
    ForeignEmployee = "ForeignEmployee",
    ForeignLegislation = "ForeignLegislation",
    EndOfEmployment = "EndOfEmployment",
    EmployerSubmission = "EmployerSubmission",
    WorkIntervals = "WorkIntervals",
    Tabs = "Tabs",
    InsuranceAmountOverview = "InsuranceAmountOverview",
    IncomeTaxOverview = "IncomeTaxOverview",
    EmployeeHeader = "EmployeeHeader",
    AdvanceTax = "AdvanceTax",
    SupportedChildren = "SupportedChildren",
    SupportingPersons = "SupportingPersons",
    WithholdingTaxOverview = "WithholdingTaxOverview",
    AnnualPaymentOverview = "AnnualPaymentOverview",
    AnnualTaxSettlement = "AnnualTaxSettlement",
    AnnualTaxSettlement_2 = "AnnualTaxSettlement_2",
    AnnualTaxSettlement_3 = "AnnualTaxSettlement_3",
    EmploymentHeader = "EmploymentHeader",
    EmploymentMetadata = "EmploymentMetadata",
    WorkingPlace = "WorkingPlace",
    TemporaryAssignment = "TemporaryAssignment",
    EmploymentPolicy = "EmploymentPolicy",
    WorkingPattern = "WorkingPattern",
    AttendanceOverview = "AttendanceOverview",
    Preparations = "Preparations",
    IncomeOverview = "IncomeOverview",
    ELDP = "ELDP",
    CalculationBase = "CalculationBase",
    InsuranceAndBonuses = "InsuranceAndBonuses",
}

export const EXPORT_XML_ACTION_ID = "exportXml";
export const SUBMIT_XML_ACTION_ID = "submitAction";
export const getExportXMLActionBaseDef = (): IToolbarItem & Required<Pick<IToolbarItem, "iconName">> => ({
    id: EXPORT_XML_ACTION_ID,
    itemType: ToolbarItemType.Icon,
    iconName: "ExportSubmissionXML",
    label: i18next.t("Submission:ExportToXML"),
});
export const getSubmitXMLActionBaseDef = (): IToolbarItem & Required<Pick<IToolbarItem, "iconName">> => ({
    id: SUBMIT_XML_ACTION_ID,
    itemType: ToolbarItemType.Icon,
    iconName: "SubmitInBulk",
    label: i18next.t("Submission:ExportXmlDialogTitleMultiple"),
});

export interface IGetSubmissionDefArgs {
    type: PrSubmissionTypeCode;
    subType?: PrSubmissionSubtypeCode;
    isSingleEmployeeView?: boolean;
}

export function getSubmissionTypeTableDef(): TFieldDefinition {
    return {
        label: i18next.t("Submission:Table.Type"),
        formatter: (val: TValue) => {
            return getMainTypeLabel(val as PrSubmissionTypeCode);
        },
    };
}

export function getSubmissionSubTypeTableDef(): TFieldDefinition {
    return {
        label: i18next.t("Submission:Table.SubType"),
        formatter: (val: TValue, args: IFormatOptions<IPrSubmissionEntity, IPrSubmissionEntity>) => {
            const type = args.item.SubmissionTypeCode;
            return getSubTypeLabel(type, val as PrSubmissionSubtypeCode);
        },
        additionalProperties: [{ id: "Name" }]
    };
}

export function getSubmissionStatusTableDef(): TFieldDefinition {
    return {
        label: i18next.t("Submission:Table.Status"),
        fieldSettings: {
            displayName: "Name"
        },
        formatter: (val: TValue, args) => {
            const statusCode = args.item?.SubmissionStatus?.Code;
            return {
                tooltip: val as string,
                value: (
                    <ColoredText color={getColorFromStatus(statusCode)}>
                        {val}
                    </ColoredText>
                )
            };
        },
        additionalProperties: [{ id: "Name" }]
    };
}

export const getSubmissionFormDef = (formControl: ComponentType<any>, context: IAppContext, args?: IGetSubmissionDefArgs): IDefinition => {
    const translationFile = getDefinitions.translationFiles[0];
    const { type, subType } = args;
    const entityType = getSubmissionEntityType(type, subType);

    const form: IFormDef = {
        id: `${entityType}Form`,
        permissions: [UserPermissionCodes.PrPayroll], // todo: customer permission
        summary: [
            getNumberOursSummaryDef(translationFile),
            getEmployeeSubmissionSummaryItemDef(PrSubmissionEntity.EmployeeHeader, args?.isSingleEmployeeView),
            {
                id: PrSubmissionEntity.SubmissionTypeCode,
                label: i18next.t("Submission:Form.TypeLabel"),
                formatter: (val: TValue, args?: IFormatOptions) => {
                    return getMainTypeLabel(val as PrSubmissionTypeCode);
                },
                updateFromLiveValue: true
            },
            {
                id: PrSubmissionEntity.SubmissionSubtypeCode,
                label: i18next.t("Submission:Form.SubTypeLabel"),
                formatter: (val: TValue, args?: IFormatOptions) => {
                    return getSubTypeLabel(args?.entity?.SubmissionTypeCode, val as PrSubmissionSubtypeCode);
                },
                isVisible: (args) => {
                    return isDefined(args?.storage?.getValueByPath(PrSubmissionEntity.SubmissionSubtypeCode));
                },
                updateFromLiveValue: true
            },
            {
                id: PrSubmissionEntity.SubmissionStatusCode,
                label: i18next.t("Submission:Form.StatusLabel"),
                isVisible: not(isNewEntity),
                formatter: (val: TValue, _?: IFormatOptions) => {
                    return getEnumDisplayValue(EntityTypeName.PrSubmissionStatus, val as string);
                },
                colorFormatter: (val: TValue, _?: IFormatOptions) => {
                    return getColorFromStatus(val as PrSubmissionStatusCode);
                }
            }
        ],
        customHeaderActions: [{
            ...getExportXMLActionBaseDef(),
            isVisible: not(isNewEntity),
        }],
        isReadOnly: (args) => {
            return !!args.storage.data.entity.DateSubmitted;
        },
        ignoreVariants: true,
        additionalProperties: [
            { id: createPath(PrSubmissionEntity.EmployeeHeader, PrEmployeeEntity.FirstName) },
            { id: createPath(PrSubmissionEntity.EmployeeHeader, PrEmployeeEntity.LastName) },
            { id: PrSubmissionEntity.DateSubmitted }
        ],
        files: AttachmentsDef,
        translationFiles: getDefinitions.translationFiles,
        isDeletable: true,
        formControl,
        getItemBreadCrumbText: (storage: Model) =>
            getItemBreadCrumbsText(storage, i18next.t("Submission:NewSubmission"), i18next.t("Submission:FormTitle", { numberOurs: (storage.data as IPrSubmissionEntity)?.NumberOurs })),
        title: i18next.t("Submission:FormTitle"),
        fieldDefinition: {
            ...getNumberRangeFieldDefs(translationFile),
            [PrSubmissionEntity.SocialEvent]: {
                // just as relation to the originating social event, not editable in submission form
                isVisible: false,
                clearIfInvisible: false
            }
        },
        groups: [{
            id: PrSubmissionGroup.Main
        }]
    };

    return {
        entitySet: type,
        form
    };
};

export const getDefinitions: IGetDefinition = (_context, args: IGetSubmissionDefArgs): IDefinition => {

    const isSingleEmployeeView = args?.isSingleEmployeeView;

    const table: ISplitPageTableDef = {
        id: `${EntityTypeName.PrSubmission}Table`,
        filter: args => {
            if (!isSingleEmployeeView) {
                return "";
            }

            const employeeId = (args.storage as TSmartODataTableStorage).getCustomData().rootStorage?.data?.bindingContext?.getKey();
            return employeeId ? `${PrSubmissionEntity.EmployeeHeader}/Id eq ${employeeId}` : "";
        },
        filterBarDef: [{
            ...getDefaultFilterGroupDef(FilterBarGroup.Filters),
            defaultFilters: [
                PrSubmissionEntity.NumberOurs,
                PrSubmissionEntity.EmployeeHeader,
                PrSubmissionEntity.SubmissionType,
                PrSubmissionEntity.SubmissionStatus,
            ],
            filterDefinition: {
                [PrSubmissionEntity.NumberOurs]: {},
                ...getEmployeeFilterDef(PrSubmissionEntity.EmployeeHeader),
                ...withDisplayName(PrSubmissionEntity.SubmissionType),
                ...withDisplayName(PrSubmissionEntity.SubmissionStatus),
            },
            isValueHelp: true
        }],
        initialSortBy: [{ id: PrSubmissionEntity.NumberOurs, sort: Sort.Desc }],
        preventStoreVariant: isSingleEmployeeView,
        columns: [
            PrSubmissionEntity.NumberOurs,
            PrSubmissionEntity.EmployeeHeader,
            createPath(PrSubmissionEntity.EmployeeHeader, PrEmployeeEntity.NumberOurs),
            PrSubmissionEntity.HealthInsuranceCompany,
            PrSubmissionEntity.SubmissionType,
            PrSubmissionEntity.SubmissionSubtype,
            PrSubmissionEntity.SubmissionStatus,
            PrSubmissionEntity.DateCreated
        ],
        columnDefinition: {
            [PrSubmissionEntity.NumberOurs]: {},
            ...getEmployeeTableColumnDefs(PrSubmissionEntity.EmployeeHeader),
            [PrSubmissionEntity.HealthInsuranceCompany]: {
                label: i18next.t("Submission:Table.Institution"),
                fieldSettings: {
                    displayName: PrHealthInsuranceCompanyEntity.ShortName
                },
                disableSort: true,
                formatter: (val: TValue, args: IFormatOptions<IPrSubmissionEntity>) => {
                    const { entity } = args;
                    const type = entity.SubmissionTypeCode;
                    const csszTypes = [
                        PrSubmissionTypeCode.EmployersNotificationOfAnEmployeesBenefitApplication,
                        PrSubmissionTypeCode.EmployerDiscountOnSocialInsurance,
                        PrSubmissionTypeCode.ReportingByTheEmployerUponTerminationOfIncapacityForWork,
                        PrSubmissionTypeCode.EmployeeRegistration,
                        PrSubmissionTypeCode.MonthlyReport,
                    ];
                    if (csszTypes.includes(type)) {
                        return "ČSSZ";
                    }
                    return val as string ?? EMPTY_DASH;
                },
            },
            [PrSubmissionEntity.SubmissionType]: getSubmissionTypeTableDef(),
            [PrSubmissionEntity.SubmissionSubtype]: getSubmissionSubTypeTableDef(),
            [PrSubmissionEntity.SubmissionStatus]: getSubmissionStatusTableDef(),
            [PrSubmissionEntity.DateCreated]: {
                label: i18next.t("Submission:Table.Created"),
            }
        },
        title: i18next.t("Submission:Title"),
        additionalProperties: [{
            // always needed for TableViewMulti to get correct entity set
            id: PrSubmissionEntity.SubmissionTypeCode,
        }]
    };

    if (isSingleEmployeeView) {
        table.filterBarDef[0].defaultFilters = table.filterBarDef[0].defaultFilters.filter(filter => filter !== PrSubmissionEntity.EmployeeHeader);
        delete table.filterBarDef[0].filterDefinition[PrSubmissionEntity.EmployeeHeader];

        table.columns = table.columns.filter(col => !col.startsWith(PrSubmissionEntity.EmployeeHeader));
    } else {
        table.tabs = [
            {
                id: PrSubmissionTabs.Ready,
                title: i18next.t("Submission:Table.Ready")
            },
            {
                id: PrSubmissionTabs.Waiting,
                title: i18next.t("Submission:Table.Waiting")
            },
            {
                id: PrSubmissionTabs.Finished,
                title: i18next.t("Submission:Table.Finished")
            },
            {
                id: SECONDARY_FILTERS_ALL,
                title: i18next.t("Submission:Table.All"),
            },
        ];
        table.tabsSettings = {
            customFilter: getFilterForTabId
        };
    }

    return {
        entitySet: EntitySetName.PrSubmissions,
        table
    };
};
getDefinitions.translationFiles = [
    "Submission", "Common", "NumberRange",
    ...PrSubmissionEnumTranslationNamespaces
];
setDefByEntityType(EntityTypeName.PrSubmission, getDefinitions);